import React, { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { Input } from "./input";
import { Checkbox } from "./checkbox";
import { Textarea } from "./textarea";
import { Listbox, ListboxOption } from "./listbox";

/**
 * CatalogValueFields
 * @param {Array} catalog - Array of catalog items (with id, key, ES, dataType, enumOptions?)
 * @param {Object} values - Object with keys as index (string/number) and values as the field value
 * @param {Function} onChange - Called with (key, value)
 * @param {String} label - Optional label
 */
export default function CatalogValueFields({ catalog, values = {}, onChange, label }) {
  const [tiposOptions, setTiposOptions] = useState([]);

  useEffect(() => {
    const fetchTipos = async () => {
      const docRef = doc(db, "openData", "tiposVenues");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setTiposOptions(docSnap.data().tipos || []);
      }
    };
    if (catalog.some((item) => item.dataType === "array" && item.elementType === "string")) {
      fetchTipos();
    }
  }, [catalog]);

  const hasValue = (item) => {
    const v = values[item.id];
    return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
  };
  // Hide retired items unless the venue already filled them in (so existing
  // data still shows, with an "Inactivo" badge to signal the deprecation).
  const visibleItems = catalog.filter(
    (item) => item.activo !== false || hasValue(item),
  );

  return (
    <div className="space-y-4">
      {label && <div className="font-semibold mb-2">{label}</div>}
      {visibleItems.map((item) => {
        const value = values[item.id] ?? "";
        const isInactive = item.activo === false;
        let input;
        if (item.dataType === "boolean") {
          input = (
            <Checkbox
              color="accent"
              checked={!!value}
              onChange={(checked) => onChange(item.id, checked)}
            />
          );
        } else if (item.dataType === "number") {
          input = (
            <Input
              type="number"
              value={value}
              onChange={(e) => onChange(item.id, e.target.value === "" ? "" : Number(e.target.value))}
              className="w-32"
            />
          );
        } else if (item.dataType === "enum" && item.enumOptions) {
          input = (
            <Listbox
              value={value}
              onChange={(newValue) => onChange(item.id, newValue)}
              className="w-40"
            >
              {item.enumOptions.map((opt) => (
                <ListboxOption key={opt} value={opt}>
                  {opt}
                </ListboxOption>
              ))}
            </Listbox>
          );
        } else if (item.dataType === "array" && item.elementType === "string") {
          const selected = Array.isArray(value) ? value : [];
          const availableOptions = tiposOptions.filter(opt => !selected.includes(opt));
          const handleSelectChange = (e) => {
            const selectedOptions = Array.from(e.target.selectedOptions).map(opt => opt.value);
            onChange(item.id, [...selected, ...selectedOptions.filter(opt => !selected.includes(opt))]);
          };
          const handleRemoveChip = (opt) => {
            onChange(item.id, selected.filter((v) => v !== opt));
          };
          input = (
            <div className="w-full">
              <select
                multiple
                value={[]}
                onChange={handleSelectChange}
                className="w-full border rounded px-3 py-2 bg-white text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none shadow-sm min-h-[40px]"
                style={{ minHeight: '40px' }}
              >
                {availableOptions.length === 0 && (
                  <option disabled value="">No hay más opciones</option>
                )}
                {availableOptions.map((opt) => (
                  <option key={opt} value={opt} className="py-2">
                    {opt}
                  </option>
                ))}
              </select>
              <div className="flex flex-wrap gap-2 mt-2">
                {selected.map((opt) => (
                  <span key={opt} className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                    {opt}
                    <button
                      type="button"
                      className="ml-1 text-blue-500 hover:text-red-500 focus:outline-none"
                      onClick={() => handleRemoveChip(opt)}
                      aria-label={`Eliminar ${opt}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          );
        } else if (item.dataType === "url") {
          input = (
            <Input
              type="url"
              value={value}
              onChange={(e) => onChange(item.id, e.target.value)}
              className="w-full"
            />
          );
        } else if (item.dataType === "textarea") {
          input = (
            <Textarea
              value={value}
              onChange={(e) => onChange(item.id, e.target.value)}
              className="w-full"
            />
          );
        } else {
          // string, array, etc.
          input = (
            <Input
              type="text"
              value={value}
              onChange={(e) => onChange(item.id, e.target.value)}
              className="w-full"
            />
          );
        }
        return (
          <div key={item.id} className="flex items-center gap-4">
            <label className="w-56 font-medium flex items-center gap-2">
              <span>{item.ES || item.key}</span>
              {isInactive && (
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">
                  Inactivo
                </span>
              )}
            </label>
            {input}
          </div>
        );
      })}
    </div>
  );
}